import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    Shield,
    LogOut,
    CheckCircle2,
    CreditCard,
    Users,
    UserCheck,
    ArrowRight,
    AlertCircle,
    Building2,
    Check,
    Calendar
} from 'lucide-react';
import './SuperadminDashboard.css';
import './AdminDashboard.css';

const getPlanItems = (plan) => {
    if (!plan) return [];
    if (Array.isArray(plan.item_ids) && plan.item_ids.length > 0) {
        return plan.item_ids.map(item => (typeof item === 'object' && item !== null) ? item : { _id: item, item_code: 'ITEM', item_name: 'Item' });
    }
    if (plan.item_id) {
        const item = plan.item_id;
        return [(typeof item === 'object' && item !== null) ? item : { _id: item, item_code: 'ITEM', item_name: 'Item' }];
    }
    return [];
};

const AdminDashboard = () => {
    const navigate = useNavigate();
    const currentUser = localStorage.getItem('currentUser') || 'Admin User';
    const companyName = localStorage.getItem('companyName') || 'Organization Admin';
    const storedCompanyId = localStorage.getItem('companyId') || '';
    const [companyId, setCompanyId] = useState(storedCompanyId);

    const [company, setCompany] = useState(null);
    const [subscription, setSubscription] = useState(null);
    const [history, setHistory] = useState([]);
    const [availablePlans, setAvailablePlans] = useState([]);
    const [companyUsers, setCompanyUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [submittingPlanId, setSubmittingPlanId] = useState(null);
    const [actionMessage, setActionMessage] = useState({ type: '', text: '' });

    const serverUrl = process.env.REACT_APP_SERVER_BASE_URL || 'http://localhost:8080';

    useEffect(() => {
        const userRole = (localStorage.getItem('userRole') || '').toLowerCase();
        if (userRole !== 'superadmin' && userRole !== 'admin') {
            navigate('/dashboard', { replace: true });
            return;
        }
        fetchData();
    }, []);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            let activeCompanyId = companyId;

            // Resolve company list & company details
            const compRes = await fetch(`${serverUrl}/api/companies`);
            if (compRes.ok) {
                const comps = await compRes.json();
                if (comps.length > 0) {
                    const currentComp = activeCompanyId ? comps.find(c => c._id === activeCompanyId) : comps[0];
                    if (currentComp) {
                        activeCompanyId = currentComp._id;
                        setCompanyId(activeCompanyId);
                        setCompany(currentComp);
                        localStorage.setItem('companyId', activeCompanyId);
                    }
                }
            }

            const [subRes, usersRes, plansRes] = await Promise.all([
                activeCompanyId ? fetch(`${serverUrl}/api/subscriptions/company/${activeCompanyId}`) : Promise.resolve(null),
                activeCompanyId ? fetch(`${serverUrl}/api/admin-users?companyId=${activeCompanyId}`) : Promise.resolve(null),
                fetch(`${serverUrl}/api/subscription-plans`)
            ]);

            if (subRes && subRes.ok) {
                const subData = await subRes.json();
                setSubscription(subData.subscription);
                setHistory(subData.history || []);
            }

            if (usersRes && usersRes.ok) {
                const usersData = await usersRes.json();
                setCompanyUsers(usersData);
            }

            if (plansRes && plansRes.ok) {
                const plansData = await plansRes.json();
                setAvailablePlans(plansData);
            }
        } catch (err) {
            console.log('Server unreachable, using fallback admin dashboard data');
            const fallbackComp = {
                _id: companyId || 'comp_1',
                name: companyName || 'Federal Steel Industries',
                code: 'FSI-001',
                maxUsers: 20
            };
            setCompany(fallbackComp);

            setSubscription({
                _id: 'sub_demo_1',
                status: 'ACTIVE',
                plans: [{
                    plan_id: {
                        _id: 'plan_cu_1',
                        plan_name: 'Copper Standard Monthly',
                        rate: 5000,
                        currency: 'USD',
                        billing_interval: 'MONTH',
                        item_id: { item_name: 'Copper Metallurgy Domain' }
                    }
                }],
                subscription_start_date: new Date(Date.now() - 30 * 86400000).toISOString()
            });

            setAvailablePlans([
                {
                    _id: 'plan_cu_1',
                    plan_name: 'Copper Standard Monthly',
                    rate: 5000,
                    currency: 'USD',
                    billing_interval: 'MONTH',
                    item_id: { item_name: 'Copper Metallurgy', item_code: 'COPPER' }
                },
                {
                    _id: 'plan_fe_1',
                    plan_name: 'Iron & Steel Enterprise',
                    rate: 12000,
                    currency: 'USD',
                    billing_interval: 'YEAR',
                    item_id: { item_name: 'Iron & Steel Metallurgy', item_code: 'IRON' }
                }
            ]);

            setCompanyUsers([
                { _id: 'u1', username: 'admin_user', role: 'Admin', email: 'admin@company.com' },
                { _id: 'u2', username: 'quality_lead', role: 'Quality Manager', email: 'qm@company.com' },
                { _id: 'u3', username: 'floor_sup', role: 'Supervisor', email: 'sup@company.com' },
                { _id: 'u4', username: 'op_mike', role: 'Shop Floor Operator', email: 'operator@company.com' }
            ]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubscribe = async (planId) => {
        let activeCompanyId = companyId || localStorage.getItem('companyId');
        setSubmittingPlanId(planId);
        setActionMessage({ type: '', text: '' });

        try {
            const res = await fetch(`${serverUrl}/api/subscriptions/subscribe`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ companyId: activeCompanyId, planId })
            });

            const data = await res.json();

            if (!res.ok) {
                setActionMessage({ type: 'error', text: data.message || 'Subscription request failed.' });
                return;
            }

            setSubscription(data.subscription);
            if (data.history) setHistory(data.history);
            setActionMessage({ type: 'success', text: 'Subscription activated successfully!' });
        } catch (err) {
            setActionMessage({ type: 'error', text: 'Failed to connect to subscription server.' });
        } finally {
            setSubmittingPlanId(null);
        }
    };

    const handleLogout = () => {
        localStorage.clear();
        navigate('/login');
    };

    const activePlan = subscription?.plans?.[0]?.plan_id;
    const hasActiveSub = subscription && subscription.status === 'ACTIVE';

    const maxUsersLimit = company?.maxUsers !== undefined ? company.maxUsers : 20;
    const totalUsersUsed = companyUsers.length;
    const quotaPercentage = Math.min(Math.round((totalUsersUsed / maxUsersLimit) * 100), 100);

    // Role breakdown
    const adminCount = companyUsers.filter(u => (u.role || '').toLowerCase() === 'admin').length;
    const qmCount = companyUsers.filter(u => (u.role || '').toLowerCase() === 'quality manager').length;
    const supervisorCount = companyUsers.filter(u => (u.role || '').toLowerCase() === 'supervisor').length;
    const operatorCount = companyUsers.filter(u =>
        (u.role || '').toLowerCase() === 'shop floor operator' || (u.role || '').toLowerCase() === 'operator'
    ).length;

    return (
        <div className="dashboard-page-container">
            {/* Navigation Header */}
            <motion.nav
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="dashboard-nav glass-panel"
            >
                <div className="nav-brand">
                    <div className="brand-icon">
                        <Building2 size={22} color="var(--primary-color)" />
                    </div>
                    <div>
                        <h2 className="text-gradient" style={{ fontSize: '20px', margin: 0, fontWeight: 700 }}>
                            {company?.name || companyName} — Admin Dashboard
                        </h2>
                    </div>
                </div>
                <div className="nav-user">
                    <span className="role-badge admin">{currentUser}</span>
                    <button onClick={handleLogout} className="btn-secondary" style={{ padding: '8px 16px' }}>
                        <LogOut size={16} /> Logout
                    </button>
                </div>
            </motion.nav>

            {/* Action Notification Message */}
            {actionMessage.text && (
                <div
                    className="glass-panel"
                    style={{
                        padding: '12px 20px',
                        margin: '16px 0',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        background: actionMessage.type === 'error' ? '#fee2e2' : '#dcfce7',
                        color: actionMessage.type === 'error' ? '#991b1b' : '#166534',
                        borderColor: actionMessage.type === 'error' ? '#fca5a5' : '#86efac'
                    }}
                >
                    {actionMessage.type === 'error' ? <AlertCircle size={18} /> : <CheckCircle2 size={18} />}
                    <span style={{ fontSize: '14px', fontWeight: 600 }}>{actionMessage.text}</span>
                </div>
            )}

            {/* 2 Primary Summary Cards */}
            <div className="dashboard-grid-2col" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '24px', margin: '24px 0' }}>

                {/* CARD 1: SUBSCRIPTION CARD */}
                <motion.div
                    whileHover={{ y: -4 }}
                    className="glass-panel"
                    style={{ padding: '24px', borderRadius: '16px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', position: 'relative', overflow: 'hidden' }}
                >
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div className="stat-icon" style={{ background: 'rgba(14, 165, 233, 0.12)', color: 'var(--primary-color)', padding: '10px', borderRadius: '12px' }}>
                                    <CreditCard size={24} />
                                </div>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>Company Subscription</h3>
                                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Active Plan & Billing Details</span>
                                </div>
                            </div>
                            <span className={`status-tag ${hasActiveSub ? 'active' : 'inactive'}`}>
                                {subscription?.status || 'NO ACTIVE SUBSCRIPTION'}
                            </span>
                        </div>

                        {hasActiveSub && activePlan ? (
                            <div style={{ marginTop: '12px' }}>
                                <h4 className="text-gradient" style={{ fontSize: '22px', margin: '0 0 6px 0', fontWeight: 700 }}>
                                    {activePlan.plan_name || activePlan.name || 'Standard Plan'}
                                </h4>
                                <div style={{ fontSize: '20px', fontWeight: 800, color: 'var(--primary-color)', fontFamily: 'monospace', marginBottom: '16px' }}>
                                    {activePlan.currency || 'USD'} {activePlan.rate ? Number(activePlan.rate).toLocaleString() : '—'}
                                    <span style={{ fontSize: '13px', color: 'var(--text-secondary)', fontWeight: 500 }}> / {activePlan.billing_interval?.toLowerCase() || 'month'}</span>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px', borderTop: '1px solid var(--panel-border)', paddingTop: '12px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ color: 'var(--text-secondary)' }}>Included Features:</span>
                                        <span style={{ fontWeight: 600, textAlign: 'right' }}>
                                            {getPlanItems(activePlan).map(i => i.item_name || i.item_code).join(', ') || 'Metallurgy Domain'}
                                        </span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ color: 'var(--text-secondary)' }}>Start Date:</span>
                                        <span>{subscription.subscription_start_date ? new Date(subscription.subscription_start_date).toLocaleDateString() : '—'}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ color: 'var(--text-secondary)' }}>End Date:</span>
                                        <span>{history?.[0]?.period_end_date ? new Date(history[0].period_end_date).toLocaleDateString() : (subscription?.subscription_end_date ? new Date(subscription.subscription_end_date).toLocaleDateString() : '—')}</span>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div style={{ padding: '20px 0', textAlign: 'center' }}>
                                <AlertCircle size={32} color="var(--text-secondary)" style={{ marginBottom: '8px' }} />
                                <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>
                                    No active subscription plan found for this organization.
                                </p>
                            </div>
                        )}
                    </div>

                    <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid var(--panel-border)' }}>
                        <button
                            onClick={() => navigate(`/admin/subscriptions/company/${companyId}`)}
                            className="btn-secondary"
                            style={{ width: '100%', justifyContent: 'center', fontSize: '13px' }}
                        >
                            View Full Subscription History <ArrowRight size={14} />
                        </button>
                    </div>
                </motion.div>

                {/* CARD 2: USERS & USER QUOTA CARD */}
                <motion.div
                    whileHover={{ y: -4 }}
                    className="glass-panel"
                    style={{ padding: '24px', borderRadius: '16px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', position: 'relative', overflow: 'hidden' }}
                >
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div className="stat-icon" style={{ background: 'rgba(99, 102, 241, 0.12)', color: 'var(--secondary-color)', padding: '10px', borderRadius: '12px' }}>
                                    <Users size={24} />
                                </div>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>Company Users</h3>
                                    {/* <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>All Roles & Limit Check</span> */}
                                </div>
                            </div>
                            <span className="company-code-badge" style={{ background: quotaPercentage >= 100 ? '#fee2e2' : 'rgba(99, 102, 241, 0.12)', color: quotaPercentage >= 100 ? '#dc2626' : 'var(--secondary-color)', fontWeight: 700 }}>
                                {totalUsersUsed} / {maxUsersLimit} Users
                            </span>
                        </div>

                        {/* Quota Progress Bar */}
                        <div style={{ marginBottom: '16px' }}>
                            {/* <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                                <span>Quota Consumption</span>
                                <span style={{ fontWeight: 700, color: quotaPercentage >= 100 ? '#dc2626' : 'var(--text-primary)' }}>{quotaPercentage}% Used</span>
                            </div> */}
                            {/* <div style={{ width: '100%', height: '8px', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '4px', overflow: 'hidden' }}>
                                <div
                                    style={{
                                        width: `${quotaPercentage}%`,
                                        height: '100%',
                                        background: quotaPercentage >= 100 ? '#ef4444' : quotaPercentage > 80 ? '#f59e0b' : 'var(--primary-color)',
                                        borderRadius: '4px',
                                        transition: 'width 0.4s ease'
                                    }}
                                />
                            </div> */}
                        </div>

                        {/* Roles Breakdown Preview */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '16px' }}>
                            <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '10px 12px', borderRadius: '10px', border: '1px solid var(--panel-border)' }}>
                                <span style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'block' }}>Admin</span>
                                <span style={{ fontSize: '18px', fontWeight: 700, color: 'var(--primary-color)' }}>{adminCount}</span>
                            </div>
                            <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '10px 12px', borderRadius: '10px', border: '1px solid var(--panel-border)' }}>
                                <span style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'block' }}>Quality Manager</span>
                                <span style={{ fontSize: '18px', fontWeight: 700, color: 'var(--secondary-color)' }}>{qmCount}</span>
                            </div>
                            <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '10px 12px', borderRadius: '10px', border: '1px solid var(--panel-border)' }}>
                                <span style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'block' }}>Supervisor</span>
                                <span style={{ fontSize: '18px', fontWeight: 700, color: '#10b981' }}>{supervisorCount}</span>
                            </div>
                            <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '10px 12px', borderRadius: '10px', border: '1px solid var(--panel-border)' }}>
                                <span style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'block' }}>Shop Floor Operator</span>
                                <span style={{ fontSize: '18px', fontWeight: 700, color: '#f59e0b' }}>{operatorCount}</span>
                            </div>
                        </div>
                    </div>

                    <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid var(--panel-border)' }}>
                        <button
                            onClick={() => navigate(`/admin/users?companyId=${companyId}`)}
                            className="btn-primary"
                            style={{ width: '100%', justifyContent: 'center' }}
                        >
                            <UserCheck size={16} /> Manage Company Users <ArrowRight size={14} />
                        </button>
                    </div>
                </motion.div>
            </div>

            {/* AVAILABLE SUBSCRIPTION PLANS SECTION */}
            {/* <div className="available-plans-section" style={{ marginTop: '36px' }}>
                <div className="section-title-bar" style={{ marginBottom: '16px' }}>
                    <h3 className="text-gradient" style={{ margin: 0, fontSize: '20px', fontWeight: 700 }}>
                        Available Subscription Plans
                    </h3>
                    <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                        Select a plan to activate domain access for your organization
                    </span>
                </div>

                <div className="plans-grid-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
                    {availablePlans.length === 0 ? (
                        <div className="glass-panel" style={{ padding: '30px', textAlign: 'center', gridColumn: '1 / -1', color: 'var(--text-secondary)' }}>
                            No subscription plans available at this time.
                        </div>
                    ) : (
                        availablePlans.map(plan => {
                            const isCurrent = (activePlan?._id || activePlan) === plan._id || activePlan?.plan_name === plan.plan_name;
                            const planItems = getPlanItems(plan);
                            return (
                                <motion.div
                                    key={plan._id}
                                    whileHover={{ scale: 1.02 }}
                                    className={`plan-card-item glass-panel ${isCurrent ? 'active-plan' : ''}`}
                                    style={{
                                        padding: '20px',
                                        borderRadius: '14px',
                                        border: isCurrent ? '2px solid var(--primary-color)' : '1px solid var(--panel-border)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justify: 'space-between',
                                        gap: '16px'
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                                        <div className="stat-icon" style={{ background: 'rgba(14, 165, 233, 0.1)', color: 'var(--primary-color)', padding: '8px', borderRadius: '10px' }}>
                                            <CreditCard size={20} />
                                        </div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'flex-end' }}>
                                            {planItems.map(item => (
                                                <span key={item._id || item.item_code || Math.random()} className="domain-tag" style={{ background: 'rgba(99, 102, 241, 0.12)', color: 'var(--primary-color)', fontSize: '11px', padding: '3px 7px', borderRadius: '6px' }}>
                                                    {item.item_name || item.item_code || 'FEATURE'}
                                                </span>
                                            ))}
                                        </div>
                                    </div>

                                    <div>
                                        <h4 style={{ margin: '0 0 6px', fontSize: '18px', fontWeight: 700 }}>{plan.plan_name}</h4>
                                        <div style={{ fontSize: '22px', fontWeight: 800, color: 'var(--primary-color)', fontFamily: 'monospace' }}>
                                            {plan.currency || 'USD'} {Number(plan.rate).toLocaleString()}
                                            <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 500 }}> / {plan.billing_interval?.toLowerCase()}</span>
                                        </div>
                                    </div>

                                    <div>
                                        {isCurrent ? (
                                            <button className="btn-secondary" disabled style={{ width: '100%', justifyContent: 'center', opacity: 0.8, background: 'rgba(16, 185, 129, 0.15)', color: '#10b981', borderColor: 'rgba(16, 185, 129, 0.3)' }}>
                                                <Check size={16} /> Current Active Plan
                                            </button>
                                        ) : hasActiveSub ? (
                                            <button className="btn-secondary" disabled style={{ width: '100%', justifyContent: 'center', fontSize: '12px', opacity: 0.65 }} title="Active subscription running.">
                                                Active Subscription Running
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => handleSubscribe(plan._id)}
                                                disabled={submittingPlanId === plan._id}
                                                className="btn-primary"
                                                style={{ width: '100%', justifyContent: 'center' }}
                                            >
                                                {submittingPlanId === plan._id ? 'Activating…' : 'Subscribe Plan'} <ArrowRight size={16} />
                                            </button>
                                        )}
                                    </div>
                                </motion.div>
                            );
                        })
                    )}
                </div>
            </div> */}

            {/* SUBSCRIPTION & BILLING HISTORY SECTION */}
            {/* <div className="history-section" style={{ marginTop: '36px' }}>
                <div className="section-title-bar" style={{ marginBottom: '16px' }}>
                    <h3 className="text-gradient" style={{ margin: 0, fontSize: '20px', fontWeight: 700 }}>
                        Subscription & Billing History
                    </h3>
                </div>

                <div className="table-panel glass-panel">
                    <table className="custom-table">
                        <thead>
                            <tr>
                                <th>Billing Period</th>
                                <th>Billing Date</th>
                                <th>Status</th>
                                <th>Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {history.length === 0 ? (
                                <tr>
                                    <td colSpan="4" style={{ textAlign: 'center', padding: '30px', color: 'var(--text-secondary)' }}>
                                        No billing history records available.
                                    </td>
                                </tr>
                            ) : (
                                history.map(h => {
                                    const startDate = h.period_start_date ? new Date(h.period_start_date).toLocaleDateString() : '—';
                                    const endDate = h.period_end_date ? new Date(h.period_end_date).toLocaleDateString() : '—';
                                    const billDate = h.billing_date ? new Date(h.billing_date).toLocaleDateString() : '—';
                                    return (
                                        <tr key={h._id}>
                                            <td>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600 }}>
                                                    <Calendar size={16} color="var(--primary-color)" />
                                                    {startDate} — {endDate}
                                                </div>
                                            </td>
                                            <td>{billDate}</td>
                                            <td>
                                                <span className={`status-tag ${h.status?.toLowerCase() || 'active'}`}>
                                                    {h.status || 'PAID'}
                                                </span>
                                            </td>
                                            <td>
                                                <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>
                                                    {activePlan?.currency || 'USD'} {Number(activePlan?.rate || 0).toLocaleString()}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div> */}
        </div>
    );
};

export default AdminDashboard;

